/**
 * Cart drawer recommendations.
 *
 * Mount point lives inside #cart-items so it scrolls with the line items.
 * simple-cart.js replaces that node's innerHTML from /cart on a full refresh,
 * which re-renders this same mount. A MutationObserver on #cart-items (not its
 * subtree) refills it. Quantity changes call cartManager.loadCartData(false);
 * adding from here does that, then refreshCartDrawer(), so the new line, the
 * count badge, and the subtotal update through the existing cart manager
 * without closing and reopening the drawer.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-pp-upsell]';
  var CACHE_PREFIX = 'pp-upsell:v1:';
  var MAX_PRODUCTS = 3;

  var memory = { signature: '', html: '', settled: false };
  var lastAddedProductId = null;
  var observer = null;
  var observerTarget = null;
  var currentController = null;
  var requestToken = 0;
  var debounceTimer = null;
  var started = false;
  var adding = false;

  function joinRoot(root, path) {
    var base = String(root || '/').replace(/\/+$/, '');
    var tail = String(path || '').replace(/^\/+/, '');
    return (base || '') + '/' + tail;
  }

  function rootOf(el) {
    return (el && el.dataset.root) || '/';
  }

  function priceToCents(value) {
    if (typeof value === 'string') {
      var trimmed = value.trim();
      if (!trimmed) return null;
      var normalized = trimmed;
      if (trimmed.indexOf(',') !== -1 && trimmed.indexOf('.') === -1) {
        normalized = trimmed.replace(',', '.');
      }
      var amount = parseFloat(normalized);
      if (!isFinite(amount)) return null;
      return Math.round(amount * 100);
    }
    if (typeof value === 'number' && isFinite(value)) {
      return Math.round(value);
    }
    return null;
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var fixed = (cents / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    var fraction = precision > 0 ? decimal + parts[1] : '';
    return whole + fraction;
  }

  function formatMoney(cents, format) {
    var moneyFormat = format || '{{amount}}';
    var value = Math.round(Number(cents));
    if (!isFinite(value)) return '';
    var active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (!active || active === 'TRY') {
      var parts = (value / 100).toFixed(2).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return '₺' + whole + ',' + parts[1];
    }
    var match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    var tokenName = match ? match[1] : 'amount';
    var formatted;
    switch (tokenName) {
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = formatWithDelimiters(value, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        formatted = formatWithDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        formatted = formatWithDelimiters(value, 2, ' ', '.');
        break;
      default:
        formatted = formatWithDelimiters(value, 2, ',', '.');
    }
    if (!match) return formatted;
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }

  function rawImage(product) {
    if (!product) return '';
    var featured = product.featured_image;
    if (typeof featured === 'string' && featured) return featured;
    if (featured && typeof featured === 'object') {
      if (featured.src) return featured.src;
      if (featured.url) return featured.url;
    }
    var images = product.images;
    if (images && images.length) {
      var first = images[0];
      if (typeof first === 'string') return first;
      if (first && (first.src || first.url)) return first.src || first.url;
    }
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var image = variants[i] && variants[i].featured_image;
      if (typeof image === 'string' && image) return image;
      if (image && typeof image === 'object' && (image.src || image.url)) {
        return image.src || image.url;
      }
    }
    return '';
  }

  function sizedImage(url, width) {
    if (!url) return '';
    var src = String(url);
    if (src.indexOf('//') === 0) src = 'https:' + src;
    try {
      var absolute = src.indexOf('http') === 0
        ? src
        : window.location.origin + (src.charAt(0) === '/' ? src : '/' + src);
      var parsed = new URL(absolute);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    }
  }

  function readCache(key) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.products)) return null;
      return parsed.products;
    } catch (error) {
      return null;
    }
  }

  function writeCache(key, products) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ products: products }));
    } catch (error) {
      /* Private mode or quota. */
    }
  }

  function slimProduct(product) {
    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      url: product.url,
      available: product.available,
      price: product.price,
      featured_image: rawImage(product) || null,
      variants: (product.variants || []).map(function (variant) {
        var image = variant.featured_image;
        var imageUrl = '';
        if (typeof image === 'string') imageUrl = image;
        else if (image && typeof image === 'object') imageUrl = image.src || image.url || '';
        return {
          id: variant.id,
          available: !!variant.available,
          price: variant.price,
          featured_image: imageUrl || null
        };
      })
    };
  }

  function productList(payload) {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.products)) return payload.products;
    return [];
  }

  function fetchProducts(url, signal) {
    return fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      signal: signal
    }).then(function (response) {
      if (!response.ok) return null;
      return response.json().then(function (payload) {
        return productList(payload).map(slimProduct);
      });
    });
  }

  function loadProducts(cacheKey, url, signal) {
    var cached = readCache(cacheKey);
    if (cached !== null) return Promise.resolve(cached);
    return fetchProducts(url, signal).then(function (products) {
      if (!products) return [];
      writeCache(cacheKey, products);
      return products;
    });
  }

  function lineNodes() {
    return Array.prototype.slice.call(
      document.querySelectorAll('#cart-items .cart-item[data-cart-item]')
    );
  }

  function signatureForIds(ids) {
    if (!ids.length) return '';
    var inCart = {};
    ids.forEach(function (id) { inCart[id] = true; });
    var seed = ids[ids.length - 1];
    if (lastAddedProductId && inCart[String(lastAddedProductId)]) {
      seed = String(lastAddedProductId);
    }
    return seed + '::' + ids.join(',');
  }

  function signatureFromDom() {
    var nodes = lineNodes();
    if (!nodes.length) return '';
    var ids = [];
    for (var i = 0; i < nodes.length; i++) {
      var id = nodes[i].getAttribute('data-product-id');
      if (!id) return '';
      ids.push(id);
    }
    return signatureForIds(ids);
  }

  function resolveCart(root, signal) {
    var nodes = lineNodes();
    if (!nodes.length) return Promise.resolve({ items: [] });
    var missing = nodes.some(function (node) {
      return !node.getAttribute('data-product-id');
    });
    if (!missing) {
      return Promise.resolve({
        items: nodes.map(function (node) {
          return {
            product_id: node.getAttribute('data-product-id'),
            variant_id: node.getAttribute('data-cart-item')
          };
        })
      });
    }
    var managerCart = window.cartManager && window.cartManager.cart;
    if (managerCart && Array.isArray(managerCart.items) && managerCart.items.length === nodes.length) {
      return Promise.resolve(managerCart);
    }
    return fetch(joinRoot(root, 'cart.js'), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      signal: signal
    }).then(function (response) {
      if (!response.ok) throw new Error('cart');
      return response.json();
    });
  }

  function seedsFor(items) {
    var ordered = items.map(function (item) {
      return String(item.product_id);
    }).filter(Boolean);
    var inCart = {};
    ordered.forEach(function (id) { inCart[id] = true; });
    var seeds = [];
    function add(id) {
      var key = id == null ? '' : String(id);
      if (!key || !inCart[key] || seeds.indexOf(key) !== -1) return;
      seeds.push(key);
    }
    add(lastAddedProductId);
    if (ordered.length) {
      add(ordered[ordered.length - 1]);
      add(ordered[0]);
      ordered.forEach(add);
    }
    return { seeds: seeds, inCart: inCart };
  }

  function normalizeProduct(product, root, inCart) {
    if (!product || product.id == null) return null;
    var id = String(product.id);
    if (inCart[id]) return null;
    var variants = Array.isArray(product.variants) ? product.variants : [];
    var variant = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i] && variants[i].available && variants[i].id) {
        variant = variants[i];
        break;
      }
    }
    if (!variant) return null;
    var cents = priceToCents(variant.price != null ? variant.price : product.price);
    if (cents == null) return null;
    var url = product.handle
      ? joinRoot(root, 'products/' + product.handle)
      : (product.url || '#');
    return {
      id: id,
      title: product.title || '',
      url: url,
      variantId: variant.id,
      cents: cents,
      image: sizedImage(rawImage(product), 160)
    };
  }

  function consider(products, root, inCart, chosen, seen) {
    (products || []).forEach(function (product) {
      if (chosen.length >= MAX_PRODUCTS) return;
      var normalized = normalizeProduct(product, root, inCart);
      if (!normalized || seen[normalized.id]) return;
      seen[normalized.id] = true;
      chosen.push(normalized);
    });
  }

  function pull(root, productId, intent, signal, inCart, chosen, seen) {
    if (chosen.length >= MAX_PRODUCTS) return Promise.resolve();
    var url = joinRoot(root, 'recommendations/products.json')
      + '?product_id=' + encodeURIComponent(productId)
      + '&limit=10&intent=' + intent;
    return loadProducts(intent + ':' + productId, url, signal).then(function (products) {
      consider(products, root, inCart, chosen, seen);
    }).catch(function (error) {
      if (error && error.name === 'AbortError') throw error;
    });
  }

  function collect(el, items, signal) {
    var root = rootOf(el);
    var seedData = seedsFor(items);
    var chosen = [];
    var seen = {};
    var chain = Promise.resolve();

    if (seedData.seeds[0]) {
      chain = chain
        .then(function () {
          return pull(root, seedData.seeds[0], 'related', signal, seedData.inCart, chosen, seen);
        })
        .then(function () {
          if (chosen.length >= MAX_PRODUCTS) return null;
          return pull(root, seedData.seeds[0], 'complementary', signal, seedData.inCart, chosen, seen);
        });
    }

    seedData.seeds.slice(1).forEach(function (productId) {
      chain = chain
        .then(function () {
          if (chosen.length >= MAX_PRODUCTS) return null;
          return pull(root, productId, 'related', signal, seedData.inCart, chosen, seen);
        })
        .then(function () {
          if (chosen.length >= MAX_PRODUCTS) return null;
          return pull(root, productId, 'complementary', signal, seedData.inCart, chosen, seen);
        });
    });

    var handle = (el.dataset.collection || '').trim();
    if (handle && el.dataset.collectionMode === 'only') {
      var onlyUrl = joinRoot(root, 'collections/' + encodeURIComponent(handle) + '/products.json') + '?limit=12';
      return loadProducts('collection:' + handle, onlyUrl, signal).then(function (products) {
        consider(products, root, seedData.inCart, chosen, seen);
        return chosen.slice(0, MAX_PRODUCTS);
      }).catch(function (error) {
        if (error && error.name === 'AbortError') throw error;
        return chosen.slice(0, MAX_PRODUCTS);
      });
    }
    if (handle) {
      chain = chain.then(function () {
        if (chosen.length >= MAX_PRODUCTS) return null;
        var url = joinRoot(root, 'collections/' + encodeURIComponent(handle) + '/products.json') + '?limit=12';
        return loadProducts('collection:' + handle, url, signal).then(function (products) {
          consider(products, root, seedData.inCart, chosen, seen);
        }).catch(function (error) {
          if (error && error.name === 'AbortError') throw error;
        });
      });
    }

    return chain.then(function () {
      return chosen.slice(0, MAX_PRODUCTS);
    });
  }

  function plusIcon() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('width', '14');
    svg.setAttribute('height', '14');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M8 3v10M3 8h10');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.75');
    path.setAttribute('stroke-linecap', 'round');
    svg.appendChild(path);
    return svg;
  }

  function renderCard(product, addLabel, priceLabel) {
    var card = document.createElement('article');
    card.className = 'pp-upsell__card';

    var link = document.createElement('a');
    link.className = 'pp-upsell__link';
    link.href = product.url;

    var media = document.createElement('span');
    media.className = 'pp-upsell__media';
    if (product.image) {
      var img = document.createElement('img');
      img.src = product.image;
      img.alt = '';
      img.width = 58;
      img.height = 58;
      img.loading = 'lazy';
      img.decoding = 'async';
      media.appendChild(img);
    }
    link.appendChild(media);

    var info = document.createElement('span');
    info.className = 'pp-upsell__info';
    var title = document.createElement('span');
    title.className = 'pp-upsell__title';
    title.textContent = product.title;
    var price = document.createElement('span');
    price.className = 'pp-upsell__price';
    price.textContent = priceLabel;
    info.appendChild(title);
    info.appendChild(price);
    link.appendChild(info);

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'pp-upsell__add';
    button.setAttribute('data-pp-upsell-add', '');
    button.setAttribute('data-variant-id', String(product.variantId));
    button.setAttribute('aria-label', addLabel + ': ' + product.title);
    button.appendChild(plusIcon());

    card.appendChild(link);
    card.appendChild(button);
    return card;
  }

  function mount() {
    return document.querySelector(SELECTOR);
  }

  function setNote(el, message) {
    if (!el) return;
    var note = el.querySelector('[data-pp-upsell-note]');
    if (!note) return;
    note.textContent = message || '';
    note.hidden = !message;
  }

  function hide(el) {
    if (!el) return;
    el.hidden = true;
    el.setAttribute('aria-busy', 'false');
    delete el.dataset.signature;
  }

  function showSkeleton(el) {
    var list = el.querySelector('[data-pp-upsell-list]');
    if (!list || list.querySelector('.pp-upsell__skeleton') || list.querySelector('[data-pp-upsell-add]')) {
      el.hidden = false;
      el.setAttribute('aria-busy', 'true');
      return;
    }
    list.textContent = '';
    var wrap = document.createElement('div');
    wrap.className = 'pp-upsell__skeleton';
    wrap.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < MAX_PRODUCTS; i++) {
      var row = document.createElement('div');
      row.className = 'pp-upsell__skeleton-card';
      wrap.appendChild(row);
    }
    list.appendChild(wrap);
    el.hidden = false;
    el.setAttribute('aria-busy', 'true');
  }

  function applyMemory(el) {
    if (!memory.html) {
      hide(el);
      return;
    }
    var list = el.querySelector('[data-pp-upsell-list]');
    if (list && !list.querySelector('[data-pp-upsell-add]')) {
      list.innerHTML = memory.html;
    }
    el.hidden = false;
    el.setAttribute('aria-busy', 'false');
    el.dataset.signature = memory.signature;
  }

  function paint(el, products, signature) {
    var list = el.querySelector('[data-pp-upsell-list]');
    if (!list) return;
    if (!products.length) {
      memory = { signature: signature, html: '', settled: true };
      list.textContent = '';
      hide(el);
      return;
    }
    var addLabel = el.dataset.addLabel || 'Sepete ekle';
    var moneyFormat = el.dataset.moneyFormat || '{{amount}}';
    var fragment = document.createDocumentFragment();
    products.forEach(function (product) {
      fragment.appendChild(renderCard(product, addLabel, formatMoney(product.cents, moneyFormat)));
    });
    list.textContent = '';
    list.appendChild(fragment);
    el.hidden = false;
    el.setAttribute('aria-busy', 'false');
    el.dataset.signature = signature;
    memory = { signature: signature, html: list.innerHTML, settled: true };
  }

  function render(token, signal) {
    var el = mount();
    if (!el || signal.aborted || token !== requestToken) return Promise.resolve();
    if (!lineNodes().length) {
      hide(el);
      memory = { signature: '', html: '', settled: false };
      return Promise.resolve();
    }

    var domSignature = signatureFromDom();
    if (domSignature && memory.settled && memory.signature === domSignature) {
      applyMemory(el);
      return Promise.resolve();
    }

    return resolveCart(rootOf(el), signal).then(function (cart) {
      if (signal.aborted || token !== requestToken) return null;
      var items = (cart && cart.items) || [];
      if (!items.length) {
        hide(el);
        memory = { signature: '', html: '', settled: false };
        return null;
      }
      var signature = signatureForIds(items.map(function (item) {
        return String(item.product_id);
      }).filter(Boolean));
      if (memory.settled && memory.signature === signature) {
        applyMemory(el);
        return null;
      }
      if (!el.querySelector('[data-pp-upsell-add]')) showSkeleton(el);
      else el.setAttribute('aria-busy', 'true');
      return collect(el, items, signal).then(function (products) {
        if (signal.aborted || token !== requestToken) return null;
        paint(mount() || el, products, signature);
        return null;
      });
    }).catch(function (error) {
      if (error && error.name === 'AbortError') return null;
      if (token !== requestToken) return null;
      var current = mount();
      if (current && !current.querySelector('[data-pp-upsell-add]')) hide(current);
      return null;
    });
  }

  function schedule() {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(run, 160);
  }

  function run() {
    ensureObserver();
    if (currentController) currentController.abort();
    currentController = new AbortController();
    var token = ++requestToken;
    render(token, currentController.signal);
  }

  function ensureObserver() {
    var items = document.getElementById('cart-items');
    if (!items || observerTarget === items) return;
    if (observer) observer.disconnect();
    observerTarget = items;
    observer = new MutationObserver(function () {
      var el = mount();
      if (!lineNodes().length) {
        if (el) hide(el);
        memory = { signature: '', html: '', settled: false };
      } else if (el && memory.settled && signatureFromDom() === memory.signature) {
        applyMemory(el);
      }
      schedule();
    });
    observer.observe(items, { childList: true });
  }

  function numericId(value) {
    var parsed = Number(value);
    return isFinite(parsed) && Math.round(parsed) === parsed && Math.abs(parsed) <= 9007199254740991
      ? parsed
      : value;
  }

  function refreshSideCart(item) {
    var manager = window.cartManager;
    if (!manager) {
      document.dispatchEvent(new CustomEvent('product:added', { detail: { item: item } }));
      return Promise.resolve();
    }
    var root = '/';
    var mounted = mount();
    if (mounted) root = rootOf(mounted);
    return fetch(joinRoot(root, 'cart.js'), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) throw new Error('cart');
      return response.json();
    }).then(function (cart) {
      manager.cart = cart;
      if (typeof manager.updateCartCount === 'function') manager.updateCartCount();
      var subtotal = document.getElementById('cart-subtotal');
      if (subtotal && cart && cart.total_price != null) {
        subtotal.textContent = formatMoney(cart.total_price, mounted && mounted.dataset.moneyFormat);
      }
      if (typeof manager.refreshCartDrawer === 'function') return manager.refreshCartDrawer();
      return null;
    }).catch(function () {
      if (typeof manager.refreshCartDrawer === 'function') return manager.refreshCartDrawer();
      return null;
    });
  }

  function setButtonsDisabled(el, disabled) {
    if (!el) return;
    el.querySelectorAll('[data-pp-upsell-add]').forEach(function (button) {
      button.disabled = disabled;
    });
  }

  function addVariant(button) {
    if (adding || button.disabled) return;
    var el = button.closest(SELECTOR);
    var variantId = button.getAttribute('data-variant-id');
    if (!el || !variantId) return;
    adding = true;
    setButtonsDisabled(el, true);
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    setNote(el, '');

    fetch(joinRoot(rootOf(el), 'cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({ id: numericId(variantId), quantity: 1 })
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        return { response: response, data: data };
      });
    }).then(function (result) {
      if (!result.response.ok) {
        var fallback = result.response.status === 422
          ? (el.dataset.soldOut || el.dataset.error || '')
          : (el.dataset.error || '');
        var message = (result.data && (result.data.description || result.data.message)) || fallback;
        setNote(el, message);
        return null;
      }
      if (result.data && result.data.product_id) {
        lastAddedProductId = String(result.data.product_id);
      }
      return refreshSideCart(result.data);
    }).catch(function () {
      setNote(el, el.dataset.error || '');
    }).then(function () {
      adding = false;
      if (button.isConnected) {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
      setButtonsDisabled(button.isConnected ? el : mount(), false);
    });
  }

  function onClick(event) {
    var button = event.target.closest('[data-pp-upsell-add]');
    if (!button) return;
    event.preventDefault();
    addVariant(button);
  }

  function rememberAdded(event) {
    var item = event.detail && event.detail.item;
    if (item && item.product_id) lastAddedProductId = String(item.product_id);
    schedule();
  }

  function boot() {
    if (started || !mount()) return;
    started = true;
    document.addEventListener('click', onClick);
    document.addEventListener('product:added', rememberAdded);
    document.addEventListener('cart:updated', rememberAdded);
    document.addEventListener('cart:refresh', function () {
      memory = { signature: '', html: '', settled: false };
      schedule();
    });
    document.addEventListener('openDrawer', function (event) {
      if (event.detail && event.detail.drawerId === 'cart') schedule();
    });
    ensureObserver();
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
